import NotificationDetailsModel, {
    NotificationDetailsDataModel,
} from "@/core/models/notification-detail";
import { environment } from "@/environments/environment";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import Response from "@/shared/models/response";
import { staticLib } from "@public/js/static-lib.js";
import axios from "axios";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import * as store from "local-storage";
import {
    computed,
    DeepReadonly,
    defineComponent,
    onMounted,
    onUnmounted,
    shallowRef,
    watch,
    watchEffect,
    WatchStopHandle,
} from "vue";

class NotificationModel {
    initialized = shallowRef(false);
    currentNotificationID = shallowRef<undefined | number>();
    notificationDetailsResponse = shallowRef<
        DeepReadonly<NotificationDetailsModel> | undefined
    >();
    isMobileSidebarOpen = shallowRef(false);
    page = shallowRef(1);
    isLoading = shallowRef(false);
    totalUnreadNews = shallowRef<undefined | number>();

    public disposers: WatchStopHandle[] = [];

    notificationsDetailsData = computed((): readonly NotificationDetailsDataModel[] => {
        return this.notificationDetailsResponse.value?.data || [];
    });

    notificationDetailsIndex = computed((): number => {
        return this.notificationsDetailsData.value.findIndex(
            (it) => it.id === this.currentNotificationID.value,
        );
    });

    canLoadMore = computed((): boolean => {
        return (
            (this.notificationDetailsResponse.value?.total || 0) >
            this.notificationsDetailsData.value.length
        );
    });

    isLastNotificationSelected = computed((): boolean => {
        return (
            this.notificationsDetailsData.value.length > 0 &&
            this.notificationsDetailsData.value[
                this.notificationsDetailsData.value.length - 1
            ].id === this.currentNotificationID.value
        );
    });

    isFirstNotificationSelected = computed((): boolean => {
        return (
            this.notificationsDetailsData.value.length > 0 &&
            this.notificationsDetailsData.value[0].id === this.currentNotificationID.value
        );
    });

    isMobileWindow = computed(() => {
        return window.matchMedia("(max-width: 768px)").matches;
    });

    mounted = () => {
        this.disposers.push(
            watch(
                () => ({
                    isLastSelected: this.isLastNotificationSelected.value,
                    canLoadMore: this.canLoadMore.value,
                }),
                ({ isLastSelected, canLoadMore }) => {
                    if (isLastSelected && canLoadMore) {
                        this.loadMore();
                    }
                },
            ),
        );

        this.disposers.push(
            watch(
                () => ({
                    currentNotificationID: this.currentNotificationID.value,
                }),
                ({ currentNotificationID }) => {
                    if (currentNotificationID) {
                        if (!this.isMobileWindow.value) {
                            document.body.classList.add("notification-detail-open");
                        }
                    } else {
                        document.body.classList.remove("notification-detail-open");
                        document.body.classList.remove("mobile-notification-open");
                    }
                    this.changeTotalUnreadNotifications();
                },
            ),
        );

        this.disposers.push(
            watch(
                () => ({
                    isMobileSidebarOpen: this.isMobileSidebarOpen.value,
                    isMobileWindow: this.isMobileWindow.value,
                }),
                ({ isMobileSidebarOpen, isMobileWindow }) => {
                    if (isMobileSidebarOpen && isMobileWindow) {
                        document.body.classList.add("mobile-notification-open");
                    } else {
                        document.body.classList.remove("mobile-notification-open");
                    }
                },
            ),
        );

        this.disposers.push(
            watchEffect(async () => {
                this.disposers.push(
                    watchEffect(() => {
                        axios
                            .get(
                                environment.apiUrl +
                                    `news/${appLanguageSwitcherModel.lang.value}`,
                                {
                                    params: {
                                        page: 0,
                                        perPage: 1000,
                                    },
                                },
                            )
                            .then((res: Response<NotificationDetailsModel>) => {
                                this.notificationDetailsResponse.value = res.data;

                                if (
                                    this.isMobileWindow.value &&
                                    this.notificationsDetailsData.value.length
                                ) {
                                    const firstNotification =
                                        this.notificationsDetailsData.value[0];
                                    this.showNotificationDetails(firstNotification.id);
                                }
                            })
                            .catch(console.log)
                            .finally(() => {
                                this.initialized.value = true;
                                this.changeTotalUnreadNotifications();
                            });
                    }),
                );
            }),
        );
    };

    unmounted = () => {
        this.disposers.forEach((dispose) => dispose());
    };

    toggleMobileSidebar = (value = !this.isMobileSidebarOpen.value) => {
        this.isMobileSidebarOpen.value = value;
    };

    showNotificationDetails = (id: number): void => {
        this.currentNotificationID.value = id;
        if (this.isNew(id)) {
            this.markAsRead(id);
        }
        if (this.isMobileWindow.value) {
            this.toggleMobileSidebar(false);
        }
    };

    hideNotificationDetails = (): void => {
        this.currentNotificationID.value = undefined;
    };

    formattedCreatedDate = (createdAt): string => {
        createdAt = new Date(Date.parse(createdAt));
        return formatDistanceToNow(createdAt, { addSuffix: true });
    };

    markAsRead = (notificationId: number): void => {
        const readNotifications = JSON.parse(store.get("readNotifications")) || [];
        readNotifications.push(notificationId);
        store.set("readNotifications", JSON.stringify(readNotifications));

        const summaryItems = document.getElementsByClassName(
            "notifications-summary__item",
        );
        if (summaryItems[notificationId - 1]) {
            summaryItems[notificationId - 1].classList.remove("new");
        }
    };

    isNew = (id: number) => {
        const readNotifications = JSON.parse(store.get("readNotifications")) || [];
        if (!readNotifications) {
            return true;
        }
        return !readNotifications.includes(id);
    };

    changeTotalUnreadNotifications = () => {
        const readNotifications: number[] =
            JSON.parse(store.get("readNotifications")) || [];
        const found = readNotifications.filter((readId) => {
            let found = false;
            this.notificationDetailsResponse.value?.data.forEach((item) => {
                if (readId === item.id) {
                    found = true;
                }
            });
            return found;
        });
        if (found.length > 0) {
            store.set("readNotifications", JSON.stringify(found));
        } else {
            store.remove("readNotifications");
        }
        this.totalUnreadNews.value =
            (this.notificationDetailsResponse.value?.total || 0) - found.length;
        if (this.totalUnreadNews.value === 0 || this.totalUnreadNews.value < 0) {
            this.totalUnreadNews.value = 0;
        }
    };

    loadMore = async () => {
        this.isLoading.value = true;
        await axios
            .get<NotificationDetailsModel>(
                environment.apiUrl + `news/${appLanguageSwitcherModel.lang.value}`,
                {
                    params: {
                        page: this.page.value + 1,
                    },
                },
            )
            .then(({ data }) => {
                if (data.data.length) {
                    this.page.value += 1;
                    this.notificationDetailsResponse.value = {
                        ...data,
                        data: [
                            ...(this.notificationDetailsResponse.value?.data || []),
                            ...data.data,
                        ],
                    };
                }
            })
            .catch(console.log)
            .finally(() => {
                this.isLoading.value = false;
            });
    };

    next = () => {
        const nextID = this.notificationDetailsIndex.value + 1;
        if (!this.notificationsDetailsData.value[nextID]) {
            return;
        }

        this.showNotificationDetails(this.notificationsDetailsData.value[nextID].id);
    };

    prev = () => {
        const prevID = this.notificationDetailsIndex.value - 1;
        if (!this.notificationsDetailsData.value[prevID]) {
            return;
        }

        this.showNotificationDetails(this.notificationsDetailsData.value[prevID].id);
    };
}

export const NotificationComponent = defineComponent({
    props: {
        active: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:active"],
    setup() {
        const model = new NotificationModel();

        onMounted(model.mounted);
        onUnmounted(model.unmounted);

        return {
            model,
        };
    },
    methods: {
        closeSidebar() {
            if (this.model.isMobileWindow.value) {
                this.model.toggleMobileSidebar(false);
            } else {
                this.$emit("update:active", false);
            }
        },
        openSidebar() {
            if (this.model.isMobileWindow.value) {
                this.model.toggleMobileSidebar(true);
            }
        },
    },
    watch: {
        active(newValue: boolean, _oldValue: boolean) {
            if (newValue) {
                staticLib.hideMainScroll(true);
                document.body.classList.add("notification-open");
            } else {
                document.body.classList.remove("mobile-notification-open");
                document.body.classList.remove("notification-open");
                staticLib.hideMainScroll();
            }
        },
    },
});
